import * as Sentry from '@sentry/react'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { AuthenticateParams, ContentType } from 'configs/constants/enums'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import set from 'lodash/set'
import qs from 'qs'
import { toast } from 'react-toastify'
import { decrypt, encrypt } from 'utils/aes'
import envConfig from '../configs'

// Network error types that should trigger retry logic
const NETWORK_ERROR_CODES = [
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
  'ERR_NETWORK_IO_SUSPENDED',
  'ECONNREFUSED',
  'ENOTFOUND',
  'ECONNRESET',
  'ETIMEDOUT'
]

// Check if error is a network-related error.
// Guard: if the server responded (any HTTP status), it's NOT a network error —
// retrying would re-hit the same failing endpoint. The previous version had
// an operator-precedence bug where a 500 with "timeout" in the body matched.
function isNetworkError(error: AxiosError): boolean {
  if (error.response) return false
  if (error.code && NETWORK_ERROR_CODES.includes(error.code)) return true
  const msg = error.message?.toLowerCase() ?? ''
  return msg.includes('network error') || msg.includes('timeout')
}

// Retry function with exponential backoff
async function retryRequest(config: InternalAxiosRequestConfig, retryCount = 0): Promise<any> {
  const maxRetries = 2
  const baseDelay = 1000 // 1 second

  if (retryCount >= maxRetries) {
    throw new Error('Max retries exceeded')
  }

  // Exponential backoff: 1s, 2s, 4s
  const delay = baseDelay * Math.pow(2, retryCount)

  await new Promise(resolve => setTimeout(resolve, delay))

  try {
    return await axiosInstance(config)
  } catch (error) {
    if (isNetworkError(error as AxiosError)) {
      return retryRequest(config, retryCount + 1)
    }
    throw error
  }
}

const axiosInstance = axios.create({
  baseURL: envConfig.apiBaseUrl,
  headers: {
    Accept: 'application/json;charset=UTF-8',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache'
  },
  paramsSerializer: { serialize: params => qs.stringify(params) }
})

axiosInstance.interceptors.request.use(
  function (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    if (envConfig.aes_secret_key && config?.headers?.['Content-Type'] === ContentType.JSON) {
      set(config, 'data', encrypt(config.data))
    }
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  function (response) {
    if (envConfig.aes_secret_key) {
      set(response, 'data', decrypt(response.data))
    }
    return response
  },
  async function (error: AxiosError) {
    // Handle network errors with retry logic
    if (isNetworkError(error) && error.config) {
      try {
        return await retryRequest(error.config)
      } catch (retryError) {
        // If retries failed, continue with original error handling
        console.warn('Network error: All retries failed', retryError)
      }
    }

    return Promise.reject(error)
  }
)

export const api = axiosInstance

function handleUnauthorized() {
  alert('Unauthorized! Please try again or contact support.')
  const rootURL = window?.location?.origin ?? `${window.location.protocol}//${import.meta.env.REACT_APP_BASE_PATH}/`
  // window.location.replace(rootURL)
}

const handleUnauthorizedWithDebounce = debounce(handleUnauthorized, 500)

api.interceptors.request.use(function (config: any) {
  const token = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN)
  if (token && !config.headers.Authorization) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` }
  }

  // Organization scope auto-stamp (replaces the legacy X-Org-Id header). When
  // the caller hasn't already supplied an `orgId`/`orgIds` on the request —
  // which is most existing endpoints, especially the org-scoped CRUD surface —
  // fill it in from the active organization in localStorage. Cross-org list
  // calls (which send `orgIds` explicitly) keep their own scope.
  //
  // This param was called `teamId` while an Organization and a Team were the
  // same unit. It always carried an organization id; only the name has changed.
  // POST /organizations creates a NEW organization — by definition there's no
  // org context yet, so the auto-stamp would attach a stale id from localStorage
  // and confuse the BE's org-agnostic route resolution.
  const requestPath = (config.url ?? '').replace(/\?.*$/, '').replace(/\/+$/, '')
  const isCreateTeamRequest = (config.method ?? 'get').toLowerCase() === 'post' && requestPath === '/organizations'
  // `/player/*` is the athlete-facing surface — profile-scoped (identity comes
  // from the X-Active-Profile-* headers below) and org-agnostic on the BE, so
  // an orgId would be ignored noise.
  const isPlayerSurfaceRequest = requestPath.startsWith('/player/')
  // `/cms/profile-image` is a user-scoped avatar upload — identity comes from the
  // JWT (+ profileId), org-agnostic on the BE, so an orgId would be ignored noise.
  const isProfileImageUpload = requestPath === '/cms/profile-image'
  // `/users/me/*` are account-scoped (profiles, notifications, teams) — filtered by
  // the JWT user and org-agnostic on the BE, so a stamped orgId is just noise.
  const isAccountScopedRequest = requestPath.startsWith('/users/me')
  // `/athlete-profiles/*`, `/coach-profiles/*`, `/claim/*` are profile/account-scoped
  // (identity from the JWT + X-Active-Profile headers) and listed in the BE's
  // orgAgnosticFirstPaths, so a stamped orgId is ignored noise (e.g. social-link
  // PATCH on /athlete-profiles/{id} was carrying ?orgId pointlessly).
  const isProfileSurfaceRequest =
    requestPath.startsWith('/athlete-profiles') ||
    requestPath.startsWith('/coach-profiles') ||
    requestPath.startsWith('/claim')
  // `/dashboard/*` resolves the caller's own organizations from their memberships
  // — that is what lets it answer for all of them at once — so it names its scope
  // in explicit query params and ignores a stamped orgId.
  const isDashboardRequest = requestPath.startsWith('/dashboard')
  const activeOrgId = localStorage.getItem(AuthenticateParams.ORG_ID)
  if (
    activeOrgId &&
    !isCreateTeamRequest &&
    !isPlayerSurfaceRequest &&
    !isProfileImageUpload &&
    !isAccountScopedRequest &&
    !isDashboardRequest &&
    !isProfileSurfaceRequest
  ) {
    const params = (config.params ?? {}) as Record<string, unknown>
    // Some callers build the query straight into the url instead of `params`, so
    // check both. Stamping a second `orgId` there produces `?orgId=1&orgId=1`,
    // which the BE reads as an array and rejects as "no org context at all".
    const urlQuery = (config.url ?? '').split('?')[1] ?? ''
    const urlCarriesOrgScope = /(^|&)(orgId|orgIds|teamId|teamIds)=/.test(urlQuery)
    const carriesOrgScope = !!params.orgId || !!params.orgIds || urlCarriesOrgScope
    if (!carriesOrgScope) {
      config.params = { ...params, orgId: activeOrgId }
    }
  }

  // Profile-context headers — sent on every authed request so backend handlers
  // can resolve `request.activeProfile` and gate writes through the org-scoped
  // authorization helper. Backend treats the headers as optional, so sending
  // them on org-agnostic routes (e.g. /users/me/profiles) is safe.
  const activeProfileId = localStorage.getItem(AuthenticateParams.ACTIVE_PROFILE_ID)
  const activeProfileType = localStorage.getItem(AuthenticateParams.ACTIVE_PROFILE_TYPE)
  if (activeProfileId) {
    config.headers = { ...config.headers, 'X-Active-Profile-Id': activeProfileId }
    if (activeProfileType) {
      config.headers = { ...config.headers, 'X-Active-Profile-Type': activeProfileType }
    }
  }

  return config
})

api.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    return response
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    if (
      error?.message?.includes('401') ||
      (error?.config?.url === '/users/me' && error?.config?.url !== '/auth/login')
    ) {
      handleUnauthorizedWithDebounce()
    }

    // 402 Payment Required — billing lockout
    if (error?.response?.status === 402) {
      const message = error?.response?.data?.error?.message ?? 'Your access is suspended due to unpaid billing.'
      toast.error(message, { toastId: 'billing-locked', autoClose: false })
      // Dispatch custom event so components can react (e.g., show billing modal)
      window.dispatchEvent(new CustomEvent('billing:locked', { detail: { message } }))
    }

    return Promise.reject(error)
  }
)

export * from './releaseNote'
export * from './jira'

export enum EErrorType {
  BAD_REQUEST = '400',
  UNAUTHORIZED = '401',
  PAYMENT_REQUIRED = '402',
  FORBIDDEN = '403',
  NOT_FOUND = '404',
  UNPROCESSABLE_ENTITY = '422',
  TOO_MANY_REQUESTS = '429',
  INTERNAL_SERVER_ERROR = '500'
}

const errorTypes: EErrorType[] = [
  EErrorType.BAD_REQUEST,
  EErrorType.UNAUTHORIZED,
  EErrorType.PAYMENT_REQUIRED,
  EErrorType.FORBIDDEN,
  EErrorType.NOT_FOUND,
  EErrorType.UNPROCESSABLE_ENTITY,
  EErrorType.TOO_MANY_REQUESTS,
  EErrorType.INTERNAL_SERVER_ERROR
]

export function handleError(
  error: Error,
  filePath: string,
  functionName: string,
  keepThrowing: boolean = true,
  showToast: boolean = false,
  group: string = ''
) {
  const errorPath = `Error: ${filePath} -> ${functionName} -> error: ${error}`
  console.error(errorPath, JSON.stringify(error))

  // Check if this is a network error
  const axiosError = error as AxiosError
  const isNetworkIssue = isNetworkError(axiosError)

  if (!errorTypes.some(type => errorPath.includes(type))) {
    if (import.meta.env.REACT_APP_FLAGS_CTX_APP_NAME !== 'development') {
      if (!isEmpty(group)) {
        Sentry.setTag('group', group)
      }

      // Add network error context to Sentry
      if (isNetworkIssue) {
        Sentry.setTag('error_type', 'network_error')
        Sentry.setTag('network_code', axiosError.code || 'unknown')
      }

      Sentry.captureException({
        errorPath,
        error: JSON.stringify(error)
      })
    }
  }

  if (showToast) {
    // Provide better error messages for network issues
    if (isNetworkIssue) {
      toast.error('Connection issue detected. Please check your internet connection and try again.')
    } else {
      toast.error(error.message)
    }
  }

  if (keepThrowing) {
    throw error
  }
}
